"use client";

import { isIdentityGraphClientEnabled } from "@repo/identity";
import type { PostHog } from "posthog-js";
import { useEffect } from "react";
import { config } from "@/config";
import { hasAnalyticsCaptureConsent } from "../../lib/analytics-consent-gate";
import { MARKETING_EVENTS } from "../../lib/marketing-events";
import { createPosthogBootstrapGate } from "./bootstrap-gate";

let posthogClient: PostHog | null = null;
let initPromise: Promise<PostHog | null> | null = null;
let bootstrapDistinctId: string | undefined;
let captureOptInRequested = false;
/** Bumped on full reset so in-flight init continuations cannot assign a stale client. */
let initLifecycleGeneration = 0;
const identityBootstrapGate = createPosthogBootstrapGate();

function posthogApiKey(): string {
	return process.env.NEXT_PUBLIC_POSTHOG_KEY?.trim() ?? "";
}

export function setPosthogBootstrapDistinctId(distinctId: string): void {
	bootstrapDistinctId = distinctId.trim();
}

export function releasePosthogIdentityBootstrap(): void {
	identityBootstrapGate.release();
}

export function resetPosthogIdentityBootstrapState(): void {
	resetPosthogClientState();
}

function resetPosthogClientState(): void {
	initLifecycleGeneration += 1;
	bootstrapDistinctId = undefined;
	captureOptInRequested = false;
	posthogClient = null;
	initPromise = null;
	identityBootstrapGate.reset();
}

function startSessionRecordingIfAvailable(client: PostHog): void {
	if (typeof client.startSessionRecording === "function") {
		client.startSessionRecording();
	}
}

function stopSessionRecordingIfAvailable(client: PostHog): void {
	if (typeof client.stopSessionRecording === "function") {
		client.stopSessionRecording();
	}
}

function optInCapturingOnce(client: PostHog | null): void {
	if (!client || captureOptInRequested) {
		return;
	}
	captureOptInRequested = true;
	if (client.has_opted_in_capturing()) {
		client.set_config({ capture_pageleave: true });
		startSessionRecordingIfAvailable(client);
		return;
	}
	client.opt_in_capturing();
	client.set_config({ capture_pageleave: true });
	startSessionRecordingIfAvailable(client);
}

function buildPosthogInitConfig(distinctId?: string) {
	return {
		api_host: config.analytics.posthogHost,
		ui_host: config.analytics.posthogUiHost,
		persistence: "localStorage+cookie" as const,
		autocapture: false,
		capture_pageview: false,
		capture_pageleave: false,
		person_profiles: "identified_only" as const,
		opt_out_capturing_by_default: true,
		...(distinctId
			? {
					bootstrap: {
						distinctID: distinctId,
						/** Treat SkillQuest `sqid` as an identified id from the first init. */
						isIdentifiedID: true,
					},
				}
			: {}),
	};
}

async function ensureInitialized(): Promise<PostHog | null> {
	const key = posthogApiKey();
	if (!key) {
		return null;
	}
	// Never spin up a fresh PostHog instance without capture consent (prevents an
	// anonymous distinct_id client that blocks later bootstrap init). Revoke flows
	// use the existing client via {@link notifyConsentRevokedBeforeOptOut}.
	if (
		posthogClient == null &&
		initPromise == null &&
		!hasAnalyticsCaptureConsent()
	) {
		return null;
	}
	if (posthogClient) {
		return posthogClient;
	}
	if (!initPromise) {
		initPromise = (async () => {
			const generationAtStart = initLifecycleGeneration;
			const isStaleGeneration = (): boolean =>
				generationAtStart !== initLifecycleGeneration;

			if (
				isIdentityGraphClientEnabled() &&
				hasAnalyticsCaptureConsent()
			) {
				await identityBootstrapGate.waitForRelease();
			}
			if (isStaleGeneration() || !hasAnalyticsCaptureConsent()) {
				initPromise = null;
				return null;
			}
			const mod = await import("posthog-js");
			if (isStaleGeneration() || !hasAnalyticsCaptureConsent()) {
				initPromise = null;
				return null;
			}
			const distinctId = bootstrapDistinctId?.trim();
			if (
				isIdentityGraphClientEnabled() &&
				hasAnalyticsCaptureConsent() &&
				(distinctId == null || distinctId.length === 0)
			) {
				initPromise = null;
				return null;
			}
			const client = mod.default;
			client.init(key, buildPosthogInitConfig(distinctId));
			if (isStaleGeneration()) {
				try {
					client.opt_out_capturing();
					client.reset();
				} catch {
					// Best-effort teardown of an init that lost the race to a full reset.
				}
				initPromise = null;
				return null;
			}
			posthogClient = client;
			return client;
		})();
	}
	return initPromise;
}

/**
 * Initializes PostHog after bootstrap `distinct_id` is set; does **not** call
 * `opt_in_capturing` — run {@link optInPosthogCapturingAfterIdentity} after
 * `alias` / `identify` so opt-in and pageviews use the same distinct id.
 */
export async function ensurePosthogClientForIdentityBootstrap(): Promise<PostHog | null> {
	return ensureInitialized();
}

export function optInPosthogCapturingAfterIdentity(): void {
	void ensureInitialized().then((client) => {
		optInCapturingOnce(client);
	});
}

export function isConfigured(): boolean {
	return Boolean(posthogApiKey());
}

const stableHooks = {
	trackEvent(event: string, data?: Record<string, unknown>) {
		if (!hasAnalyticsCaptureConsent()) {
			return;
		}
		void ensureInitialized().then((client) => {
			client?.capture(event, data);
		});
	},
};

export function AnalyticsScript() {
	useEffect(() => {
		if (!isConfigured() || !hasAnalyticsCaptureConsent()) {
			return;
		}
		void ensureInitialized().then((client) => {
			optInCapturingOnce(client);
		});
	}, []);

	return null;
}

export function useAnalytics(): typeof stableHooks {
	return stableHooks;
}

export function capturePageView(input: { url: string; title?: string }) {
	if (!hasAnalyticsCaptureConsent()) {
		return;
	}
	void ensureInitialized().then((client) => {
		if (!client) {
			return;
		}
		client.capture("$pageview", {
			$current_url: input.url,
			...(input.title != null ? { $title: input.title } : {}),
		});
	});
}

export function identify(
	id: string,
	props?: Record<string, unknown>,
): Promise<void> {
	if (!hasAnalyticsCaptureConsent()) {
		return Promise.resolve();
	}
	return ensureInitialized().then((client) => {
		client?.identify(id, props);
	});
}

export function alias(aliasId: string, distinctId: string): Promise<void> {
	if (!hasAnalyticsCaptureConsent()) {
		return Promise.resolve();
	}
	return ensureInitialized().then((client) => {
		client?.alias(aliasId, distinctId);
	});
}

export function reset() {
	if (posthogClient) {
		posthogClient.reset();
		return;
	}
	void initPromise?.then((client) => {
		client?.reset();
	});
}

export async function getDistinctId(): Promise<string | null> {
	const client = await ensureInitialized();
	return client?.get_distinct_id() ?? null;
}

export function recordConsentChoice(payload: Record<string, unknown>): void {
	void ensureInitialized().then((client) => {
		if (!client) {
			return;
		}
		client.capture(MARKETING_EVENTS.consentChoiceMade, payload);
	});
}

export function notifyAnalyticsConsentGranted(
	payload: Record<string, unknown>,
): void {
	recordConsentChoice({
		...payload,
		flow: "analytics_granted",
	});
}

export function notifyConsentRevokedBeforeOptOut(
	payload: Record<string, unknown>,
): void {
	const finalize = (client: PostHog | null) => {
		if (!client) {
			resetPosthogClientState();
			return;
		}
		client.capture(MARKETING_EVENTS.consentChoiceMade, {
			...payload,
			flow: "analytics_revoked",
		});
		stopSessionRecordingIfAvailable(client);
		client.opt_out_capturing();
		client.reset();
		resetPosthogClientState();
	};

	if (posthogClient) {
		finalize(posthogClient);
		return;
	}
	if (initPromise) {
		void initPromise.then(finalize);
		return;
	}
	resetPosthogClientState();
}

export function optOut() {
	const finalize = (client: PostHog | null) => {
		if (client) {
			stopSessionRecordingIfAvailable(client);
		}
		client?.opt_out_capturing();
		client?.reset();
		resetPosthogClientState();
	};

	if (posthogClient) {
		finalize(posthogClient);
		return;
	}
	void initPromise?.then((client) => {
		finalize(client);
	});
}
